import { useEffect, useMemo, useState } from "react";
import type { TalentApi } from "components/Screens/Marketplace/Customer/TalentApiContext";
import type { ArbiterRole, TalentFacet, TalentFacets } from "components/Screens/Marketplace/Customer/types";
import { TALENT_ROLES } from "components/Screens/Marketplace/Customer/TalentNetwork/talentRoles";
import { roleOptions as arbiterRoleOptions } from "components/Screens/Marketplace/Customer/TalentNetwork/roleOptions";
import { cleanTools, isProTool, toolKey, toolLabels, uniqueTools } from "components/Screens/Marketplace/Customer/utils/tools";

export interface FilterOption {
  value: string;
  label: string;
  description?: string;
  /** A heading the option is listed under (the Role filter groups by industry). */
  group?: string;
}

/** One option per value, most common first (prototype filterOptions). The counts are not shown (product, 2026-10-22). */
function facetOptions(facets: TalentFacet[], keep: (label: string) => boolean = () => true): FilterOption[] {
  return facets
    .map((f) => ({ label: toolLabels(f.variants).values().next().value || f.key, count: f.count }))
    .filter((o) => keep(o.label))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .map(({ label }) => ({ value: label, label }));
}

/**
 * The Apps options with the customer's own apps in them: an app the pool lists takes the customer's spelling (so
 * a pick spelt "dentrix" ticks "Dentrix"; the API matches either way); one it does not list goes first, so a
 * preselected app can always be seen and unticked.
 */
export function withPickedTools(options: FilterOption[], picked: string[]): FilterOption[] {
  const mine = new Map(uniqueTools(picked).map((t) => [toolKey(t), t]));
  if (!mine.size) return options;
  const listed = new Set(options.map((o) => toolKey(o.value)));
  const extra = [...mine.entries()].filter(([k]) => !listed.has(k)).map(([, t]) => ({ value: t, label: t }));
  return [...extra, ...options.map((o) => ({ ...o, value: mine.get(toolKey(o.value)) ?? o.value }))];
}

/**
 * Role options are the arbiter roles (title + description, grouped by industry); the picked titles are sent
 * as arbiterRole. Tools are built from what talent in the network actually list, most common first
 * (QA PO-4 / PO-7), generic tools left out. Fallbacks: the fixed role list, and the tool catalogue.
 */
export default function useTalentFilterOptions(api: Pick<TalentApi, "fetchTalentFacets" | "fetchTools" | "fetchRoles">) {
  const [facets, setFacets] = useState<TalentFacets | null>(null);
  const [catalogue, setCatalogue] = useState<string[]>([]);
  const [roles, setRoles] = useState<ArbiterRole[] | null>(null);

  useEffect(() => {
    let live = true;
    api
      .fetchRoles()
      .then((list) => live && setRoles(Array.isArray(list) ? list : []))
      .catch(() => live && setRoles([]));
    api
      .fetchTalentFacets()
      .then((f) => live && setFacets(f))
      .catch(() =>
        api
          .fetchTools()
          .then((tools) => live && setCatalogue(cleanTools(tools.filter((t) => t.active !== false).map((t) => t.name))))
          .catch(() => undefined)
      );
    return () => {
      live = false;
    };
    // Loaded once per page visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(() => {
    const fromApi = roles ? arbiterRoleOptions(roles) : [];
    return {
      roleOptions: fromApi.length ? fromApi : (TALENT_ROLES as FilterOption[]),
      toolOptions: facets
        ? facetOptions(facets.tools, isProTool)
        : [...catalogue].sort((a, b) => a.localeCompare(b)).map((name) => ({ value: name, label: name })),
    };
  }, [facets, catalogue, roles]);
}
